import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface EvolutionMechanism {
  name: string;
  english: string;
  description: string;
  icon: string;
  color: string;
}

interface EvidenceType {
  name: string;
  english: string;
  description: string;
  icon: string;
}

interface Milestone {
  era: string;
  event: string;
  time: string;
  color: string;
}

interface EvolutionBioTemplateProps {
  topic: string;
  subtitle: string;
  mechanisms: EvolutionMechanism[];
  evidences: EvidenceType[];
  milestones: Milestone[];
  treeLabels: { name: string; x: number; y: number }[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: EvolutionBioTemplateProps = {
  topic: '进化生物学与自然选择',
  subtitle: 'Evolutionary Biology & Natural Selection',
  mechanisms: [
    {
      name: '自然选择',
      english: 'Natural Selection',
      description: '适者生存——环境压力筛选有利性状',
      icon: '🦁',
      color: '#f59e0b',
    },
    {
      name: '基因漂变',
      english: 'Genetic Drift',
      description: '小种群中等位基因频率的随机波动',
      icon: '🎲',
      color: '#fbbf24',
    },
    {
      name: '基因流',
      english: 'Gene Flow',
      description: '种群间个体迁移导致的基因交换',
      icon: '🌊',
      color: '#fcd34d',
    },
    {
      name: '突变',
      english: 'Mutation',
      description: 'DNA序列随机改变，进化的原材料',
      icon: '🧬',
      color: '#fde68a',
    },
  ],
  evidences: [
    {
      name: '化石记录',
      english: 'Fossil Record',
      description: '地层中保存的古生物遗迹',
      icon: '🦴',
    },
    {
      name: '同源结构',
      english: 'Homologous Structures',
      description: '不同物种相似的解剖学结构',
      icon: '🦎',
    },
    {
      name: '分子证据',
      english: 'Molecular Evidence',
      description: 'DNA/蛋白质序列的比较分析',
      icon: '🔬',
    },
    {
      name: '生物地理',
      english: 'Biogeography',
      description: '物种在不同地域的分布规律',
      icon: '🌍',
    },
  ],
  milestones: [
    {
      era: '生命起源',
      event: '最早的原核生物出现',
      time: '~38亿年前',
      color: '#f59e0b',
    },
    {
      era: '寒武纪大爆发',
      event: '动物门类大规模涌现',
      time: '~5.4亿年前',
      color: '#fbbf24',
    },
    {
      era: '恐龙灭绝',
      event: '白垩纪末期大灭绝事件',
      time: '~6600万年前',
      color: '#fcd34d',
    },
    {
      era: '人类出现',
      event: '智人在非洲大陆演化而来',
      time: '~30万年前',
      color: '#fde68a',
    },
  ],
  treeLabels: [
    { name: '细菌', x: 60, y: 30 },
    { name: '古菌', x: 160, y: 30 },
    { name: '真菌', x: 250, y: 30 },
    { name: '植物', x: 340, y: 30 },
    { name: '动物', x: 430, y: 30 },
  ],
  stats: [
    { value: '870万+', label: '地球现存物种估计数' },
    { value: '99.9%', label: '曾存在物种已灭绝' },
    { value: '38亿年', label: '地球生命演化历程' },
  ],
  bottomQuote:
    '没有什么比进化论更能解释生命的多样性——达尔文的洞见改变了人类认识世界的方式',
};

export const EvolutionBioTemplate: React.FC<
  Partial<EvolutionBioTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const mechStart = 30;
  const evidenceStart = 170;
  const treeStart = 200;
  const milestoneStart = 320;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const treeDraw = interpolate(
    frame,
    [treeStart, treeStart + 100],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #2a1a03 0%, #18100a 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Floating organic shape particles */}
      {Array.from({ length: 50 }).map((_, i) => {
        const x = (i * 307) % 1920;
        const y = (i * 173) % 1080;
        const size = 3 + (i % 5) * 1.5;
        const drift = Math.sin(frame * 0.02 + i * 0.7) * 8;
        const pulse = 0.05 + 0.04 * Math.sin(frame * 0.035 + i * 0.5);
        const colors = ['#f59e0b', '#fbbf24', '#fcd34d', '#fde68a'];
        const isOval = i % 3 === 0;
        return (
          <div
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: x,
              top: y + drift,
              width: isOval ? size * 1.6 : size,
              height: size,
              borderRadius: isOval ? '50%' : '40%',
              background: colors[i % colors.length],
              opacity: pulse,
              transform: `rotate(${i * 37}deg)`,
              boxShadow: `0 0 ${size * 2}px ${colors[i % colors.length]}25`,
            }}
          />
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#92400e',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#fde68a',
            flexShrink: 0,
          }}
        >
          🦕 进化生物学
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#fef3c7',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 26,
            color: '#94a3b8',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: Evolution Mechanisms ===== */}
      <div style={{ position: 'absolute', top: 100, left: 36, width: 560 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          进化机制
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {p.mechanisms.map((mech, i) => {
            const s = spring({
              frame: frame - (mechStart + i * 16),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  background: '#1c1208',
                  borderRadius: 14,
                  padding: '12px 16px',
                  border: `1.5px solid ${mech.color}30`,
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    background: `${mech.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 28,
                    flexShrink: 0,
                    boxShadow: `0 0 18px ${mech.color}30`,
                  }}
                >
                  {mech.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <span style={{ fontSize: 22, fontWeight: 800, color: mech.color }}>
                      {mech.name}
                    </span>
                    <span style={{ fontSize: 22, color: '#64748b' }}>
                      {mech.english}
                    </span>
                  </div>
                  <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 2 }}>
                    {mech.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Evidence Types */}
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            进化证据
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {p.evidences.map((ev, i) => {
              const s = spring({
                frame: frame - (evidenceStart + i * 14),
                fps,
                config: { damping: 14, stiffness: 130 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#1c1208',
                    borderRadius: 12,
                    padding: '12px 10px',
                    border: '1px solid #451a03',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <div style={{ fontSize: 26, marginBottom: 4, textAlign: 'center' }}>
                    {ev.icon}
                  </div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      color: '#fcd34d',
                      textAlign: 'center',
                    }}
                  >
                    {ev.name}
                  </div>
                  <div
                    style={{
                      fontSize: 20,
                      color: '#94a3b8',
                      marginTop: 4,
                      textAlign: 'center',
                      lineHeight: 1.4,
                    }}
                  >
                    {ev.description}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== CENTER: Phylogenetic Tree SVG ===== */}
      <div style={{ position: 'absolute', top: 100, left: 700, width: 660 }}>
        <div
          style={{
            fontSize: 26,
            fontWeight: 700,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          系统发育树 · 生命之树
        </div>
        <svg width="640" height="370" viewBox="0 0 520 300">
          <defs>
            <linearGradient id="treeGrad" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="#b45309" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#fbbf24" stopOpacity="0.2" />
            </linearGradient>
          </defs>

          {/* Main trunk */}
          <line
            x1="260" y1="280" x2="260" y2={280 - 140 * treeDraw}
            stroke="url(#treeGrad)" strokeWidth="4"
            strokeLinecap="round"
          />

          {/* Primary branches */}
          {[
            { x1: 260, y1: 180, x2: 100, y2: 100 },
            { x1: 260, y1: 180, x2: 190, y2: 90 },
            { x1: 260, y1: 160, x2: 270, y2: 80 },
            { x1: 260, y1: 150, x2: 360, y2: 80 },
            { x1: 260, y1: 140, x2: 440, y2: 70 },
          ].map((branch, i) => {
            const branchIn = interpolate(
              frame,
              [treeStart + 40 + i * 16, treeStart + 70 + i * 16],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            );
            return (
              <g key={i}>
                <line
                  x1={branch.x1}
                  y1={branch.y1}
                  x2={branch.x1 + (branch.x2 - branch.x1) * branchIn}
                  y2={branch.y1 + (branch.y2 - branch.y1) * branchIn}
                  stroke="#b4530980"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
                {/* Secondary twigs */}
                {branchIn > 0.8 && (
                  <line
                    x1={branch.x2}
                    y1={branch.y2}
                    x2={branch.x2 + (i % 2 === 0 ? -20 : 20)}
                    y2={branch.y2 - 25}
                    stroke="#b4530950"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    opacity={(branchIn - 0.8) * 5}
                  />
                )}
              </g>
            );
          })}

          {/* Leaf nodes (species labels) */}
          {p.treeLabels.map((label, i) => {
            const nodeIn = spring({
              frame: frame - (treeStart + 80 + i * 14),
              fps,
              config: { damping: 14, stiffness: 130 },
            });
            return (
              <g key={i} opacity={nodeIn}>
                <circle
                  cx={label.x + 40} cy={label.y + 22} r="8"
                  fill="#f59e0b" opacity="0.8"
                />
                <text
                  x={label.x + 40} y={label.y + 8}
                  fill="#fde68a" fontSize="17" fontWeight="700"
                  textAnchor="middle"
                >
                  {label.name}
                </text>
              </g>
            );
          })}

          {/* Root label */}
          <g
            opacity={interpolate(
              frame,
              [treeStart + 20, treeStart + 40],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            )}
          >
            <text x="260" y="298" fill="#b45309" fontSize="18" fontWeight="700" textAnchor="middle">
              LUCA · 最后共同祖先
            </text>
          </g>

          {/* Time arrow */}
          <g opacity={0.5 * treeDraw}>
            <line x1="30" y1="280" x2="30" y2="40" stroke="#78350f" strokeWidth="1.5" />
            <polygon points="26,45 30,30 34,45" fill="#78350f" />
            <text x="18" y="160" fill="#78350f" fontSize="12" textAnchor="middle"
              transform="rotate(-90, 18, 160)"
            >
              时间 →
            </text>
          </g>
        </svg>

        {/* Milestones Timeline */}
        <div style={{ marginTop: 14 }}>
          <div
            style={{
              fontSize: 26,
              fontWeight: 700,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            关键里程碑
          </div>
          <div style={{ position: 'relative', paddingLeft: 26 }}>
            {/* Timeline line */}
            <div
              style={{
                position: 'absolute',
                left: 10,
                top: 0,
                bottom: 0,
                width: 4,
                background: 'linear-gradient(180deg, #f59e0b, #78350f)',
                borderRadius: 2,
                opacity: interpolate(
                  frame,
                  [milestoneStart, milestoneStart + 30],
                  [0, 1],
                  { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
                ),
              }}
            />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {p.milestones.map((ms, i) => {
                const s = spring({
                  frame: frame - (milestoneStart + i * 18),
                  fps,
                  config: { damping: 14, stiffness: 120 },
                });
                return (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                      opacity: s,
                      transform: `translateX(${(1 - s) * 30}px)`,
                      position: 'relative',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        left: -20,
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        background: ms.color,
                        border: '3px solid #1c1208',
                        boxShadow: `0 0 12px ${ms.color}50`,
                      }}
                    />
                    <div
                      style={{
                        background: '#1c1208',
                        borderRadius: 14,
                        padding: '14px 20px',
                        border: `1px solid ${ms.color}30`,
                        flex: 1,
                        marginLeft: 12,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                        <span style={{ fontSize: 22, fontWeight: 800, color: ms.color }}>
                          {ms.era}
                        </span>
                        <span style={{ fontSize: 20, color: '#64748b' }}>
                          {ms.time}
                        </span>
                      </div>
                      <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 4 }}>
                        {ms.event}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ===== RIGHT COLUMN: Darwin's Insights ===== */}
      <div style={{ position: 'absolute', top: 100, right: 36, width: 380 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          达尔文的洞见
        </div>
        {[
          {
            title: '物种起源',
            year: '1859年',
            desc: '奠定进化论基础的划时代巨著',
            icon: '📖',
          },
          {
            title: '适者生存',
            year: '核心原理',
            desc: '环境选择最适应个体的性状',
            icon: '🏆',
          },
          {
            title: '共同祖先',
            year: '核心假说',
            desc: '所有生命源自同一祖先',
            icon: '🌳',
          },
          {
            title: '渐变演化',
            year: '核心机制',
            desc: '物种通过微小变异逐渐演化',
            icon: '🔄',
          },
        ].map((insight, i) => {
          const s = spring({
            frame: frame - (mechStart + 80 + i * 16),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#1c1208',
                borderRadius: 12,
                padding: '12px 16px',
                border: '1.5px solid #451a03',
                marginBottom: 8,
                opacity: s,
                transform: `translateY(${(1 - s) * 16}px)`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 24 }}>{insight.icon}</span>
                <span style={{ fontSize: 22, fontWeight: 800, color: '#fde68a' }}>
                  {insight.title}
                </span>
                <span style={{ fontSize: 20, color: '#64748b', marginLeft: 'auto' }}>
                  {insight.year}
                </span>
              </div>
              <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 4, paddingLeft: 34 }}>
                {insight.desc}
              </div>
            </div>
          );
        })}

        {/* Speciation diagram */}
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            物种形成模式
          </div>
          <svg width="390" height="240" viewBox="0 0 310 150">
            {/* Allopatric speciation */}
            <g
              opacity={interpolate(
                frame,
                [evidenceStart + 60, evidenceStart + 90],
                [0, 1],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              )}
            >
              {/* Single population */}
              <ellipse cx="150" cy="130" rx="44" ry="14" fill="#f59e0b30" stroke="#f59e0b" strokeWidth="1.5" />
              <text x="150" y="135" fill="#f59e0b" fontSize="15" fontWeight="700" textAnchor="middle">
                祖先种群
              </text>

              {/* Divergence arrows */}
              <line x1="125" y1="112" x2="80" y2="80" stroke="#b4530980" strokeWidth="2" />
              <line x1="175" y1="112" x2="220" y2="80" stroke="#b4530980" strokeWidth="2" />

              {/* Barrier */}
              <line
                x1="150" y1="108" x2="150" y2="55"
                stroke="#78350f" strokeWidth="3"
                strokeDasharray="6 4"
              />
              <text x="150" y="48" fill="#78350f" fontSize="16" fontWeight="700" textAnchor="middle">
                地理隔离
              </text>

              {/* Two species */}
              <ellipse cx="72" cy="65" rx="40" ry="13" fill="#fbbf2430" stroke="#fbbf24" strokeWidth="1.5" />
              <text x="72" y="70" fill="#fbbf24" fontSize="15" fontWeight="700" textAnchor="middle">
                新种A
              </text>
              <ellipse cx="228" cy="65" rx="40" ry="13" fill="#fcd34d30" stroke="#fcd34d" strokeWidth="1.5" />
              <text x="228" y="70" fill="#fcd34d" fontSize="15" fontWeight="700" textAnchor="middle">
                新种B
              </text>

              {/* Further divergence */}
              <line x1="50" y1="50" x2="38" y2="26" stroke="#b4530950" strokeWidth="1.5" />
              <line x1="94" y1="50" x2="106" y2="26" stroke="#b4530950" strokeWidth="1.5" />
              <line x1="206" y1="50" x2="194" y2="26" stroke="#b4530950" strokeWidth="1.5" />
              <line x1="250" y1="50" x2="262" y2="26" stroke="#b4530950" strokeWidth="1.5" />

              <circle cx="38" cy="22" r="7" fill="#fbbf24" opacity="0.7" />
              <circle cx="106" cy="22" r="7" fill="#fbbf24" opacity="0.7" />
              <circle cx="194" cy="22" r="7" fill="#fcd34d" opacity="0.7" />
              <circle cx="262" cy="22" r="7" fill="#fcd34d" opacity="0.7" />
            </g>

            <text x="155" y="165" fill="#64748b" fontSize="18" fontWeight="600" textAnchor="middle">
              异域物种形成 (Allopatric Speciation)
            </text>
          </svg>
        </div>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      {/* <div
        style={{
          position: 'absolute',
          bottom: 105,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#1c1208',
                borderRadius: 14,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #451a03',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div
                style={{ fontSize: 34, fontWeight: 900, color: '#f59e0b' }}
              >
                {st.value}
              </div>
              <div style={{ fontSize: 16, color: '#94a3b8', marginTop: 4 }}>
                {st.label}
              </div>
            </div>
          );
        })}
      </div> */}

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 6,
          left: 36,
          right: 36,
          background: '#1c1208',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #92400e',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>🌿</span>
          <span style={{ fontSize: 20, color: '#fef3c7', lineHeight: 1.6 }}>
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #b45309, #f59e0b, #fde68a)',
        }}
      />
    </AbsoluteFill>
  );
};
